import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { subscribeFrame } from '@/lib/universe/store'

// The tube of the ring is built from thin flat rings stacked through its thickness, so it has
// real depth when it leans. Sizes are in percent of the box width.
const LAYERS = 18
const CENTRE = 37
const TUBE = 7.5

// Base lean, and how far it leans further as the cursor crosses the window. It never spins.
const BASE_X = 63
const BASE_Z = -13
const TILT_X = 6
const TILT_Y = 9

interface Layer {
    z: number
    inner: number
    outer: number
    twist: number
    alpha: number
}

const layers: Array<Layer> = Array.from({ length: LAYERS }, (_, index) => {
    const theta = -Math.PI / 2 + (Math.PI * index) / (LAYERS - 1)
    const half = TUBE * Math.cos(theta)
    const outer = CENTRE + half

    return {
        z: TUBE * Math.sin(theta),
        inner: ((CENTRE - half) / outer) * 100,
        outer,
        twist: (theta * 180) / Math.PI * 0.9,
        // The faces turned towards the viewer catch more light than the sides
        alpha: 0.32 + 0.4 * Math.max(0, Math.sin(theta)) + 0.12 * Math.cos(theta)
    }
})

// A ring of liquid glass and chrome, drawn in light so it sits in the dark over the hole.
// It leans slowly towards the cursor and its reflections slide across it as it does.
export const GlassRing: FC = (): ReactNode => {
    const ringRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const target = { x: 0, y: 0 }
        const current = { x: 0, y: 0 }
        const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType === 'touch') return

            target.x = (event.clientX / window.innerWidth) * 2 - 1
            target.y = (event.clientY / window.innerHeight) * 2 - 1
        }

        if (fine) window.addEventListener('pointermove', onMove, { passive: true })

        const stop = subscribeFrame((frame, dt) => {
            const ring = ringRef.current

            if (!ring) return

            const follow = 1 - Math.exp(-dt * 2.2)
            const goalX = frame.reducedMotion ? 0 : target.x
            const goalY = frame.reducedMotion ? 0 : target.y

            current.x += (goalX - current.x) * follow
            current.y += (goalY - current.y) * follow

            ring.style.transform = `rotateZ(${BASE_Z}deg) rotateX(${(BASE_X - current.y * TILT_X).toFixed(3)}deg) rotateY(${(current.x * TILT_Y).toFixed(3)}deg)`
            ring.style.setProperty('--shine', `${(current.x * 28 - current.y * 14).toFixed(2)}deg`)
        })

        return () => {
            stop()
            window.removeEventListener('pointermove', onMove)
        }
    }, [])

    return (
        <div role='img' aria-label='A ring of glass and chrome, tilted in the dark' className='relative aspect-square w-full [container-type:inline-size]'>
            <div className='umbra-ring absolute inset-0'>
                <div
                    ref={ringRef}
                    style={{ transform: `rotateZ(${BASE_Z}deg) rotateX(${BASE_X}deg)` }}
                    className='absolute inset-0 [transform-style:preserve-3d] will-change-transform'
                >
                    {layers.map((layer, index) => (
                        <span
                            key={index}
                            aria-hidden='true'
                            style={{
                                '--z': layer.z.toFixed(3),
                                '--outer': layer.outer.toFixed(3),
                                '--inner': `${layer.inner.toFixed(2)}%`,
                                '--twist': `${layer.twist.toFixed(2)}deg`,
                                opacity: layer.alpha.toFixed(3)
                            } as CSSProperties}
                            className='umbra-ring-layer'
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}
